/**
 * @file guideSteps.js
 * @description Content for the in-app onboarding feature guide.
 *
 * THIS IS THE ONLY FILE YOU NEED TO EDIT TO CHANGE THE GUIDE.
 * --------------------------------------------------------------
 * To add a feature card:    add an object to GUIDE_STEPS.
 * To remove one:            delete its object.
 * To reorder:               move objects (array order = display order).
 * To re-surface the guide   bump CURRENT_GUIDE_VERSION (see below).
 * to everyone after a
 * new feature ships:
 *
 * It is data only - no rendering logic. The dialog
 * (components/guide/FeatureGuideDialog.js) renders whatever is here, and
 * utils/guideSelectors.js filters it per user capability. Keeping content
 * separate from presentation is what makes editing painless.
 *
 * Copy voice: warm + professional. Hebrew is the primary audience; keep both
 * he/en in sync. Quote in-app labels verbatim (e.g. "הזדמנויות חמות") so the
 * guide matches exactly what the user sees on screen. Use "\n" in a body to
 * force a line break (the dialog renders bodies with white-space: pre-line).
 *
 * Step shape:
 *   {
 *     id:       string  unique, stable key (used by React + analytics)
 *     Icon:     lucide-react icon component | null (header badge; null = none)
 *     accent:   string  hex color for the icon badge (defaults to brand teal)
 *     iconInline: boolean  render the icon small + faded at the START of the
 *                            title (no badge). Default false = badge above.
 *     iconGradient: [from,to] optional 2-stop gradient for the inline icon
 *                            stroke; falls back to `accent` when omitted.
 *     title:    { he, en }
 *     body:     { he, en }   short paragraph; keep it to 1-3 sentences
 *     note:     { he, en }   optional small-print line below the body, set off
 *                            with a faded "!" icon (e.g. caveats / exclusions)
 *     bodyAlign: "start"|"center"  body alignment; "start" follows the dialog
 *                            dir (right in RTL). Default "center".
 *     media:    string|null  optional screenshot path under /public/guide/
 *                            e.g. '/guide/tabs.png' - omit/null for text-only
 *     cta:      { path }|null optional in-app link; renders a "go there" button
 *     requires: string[]     capability keys; ALL must be satisfied for the
 *                            step to show. Omit/empty => visible to everyone.
 *                            Known keys (see guideSelectors.js):
 *                              'bi'                → has_active_bi_monitor
 *                              'intervention'      → intervention_enabled
 *                              'admin'             → is_admin
 *                              'recommender'       → is_recommender
 *                              'followup'          → follow_up_eligible
 *                              'additional_emails' → Professional/Enterprise tier
 *                              'website'           → website_eligible
 *                              'supporters'        → supporter_network_eligible
 *                              'labels'            → the dashboard's labels gate
 *                                                    (labelSourceFor: not lapsed)
 *   }
 */

import {
  Rocket,
  Flame,
  BellRing,
  LayoutGrid,
  Brain,
  MessageSquare,
  Phone,
  MailPlus,
  MapPinOff,
  PanelsTopLeft,
  Aperture,
  Fingerprint,
  HeartHandshake,
  Tags,
} from "lucide-react";

import { colors } from "../theme/tokens";

/**
 * Bump this whenever you want the guide to re-open for users who have already
 * seen it (e.g. after adding a major feature card). The backend stores the
 * highest version each user has dismissed; when this constant is higher, the
 * guide auto-opens once on their next authenticated load.
 *
 * Start at 1. Increment by 1. Never decrease.
 */
export const CURRENT_GUIDE_VERSION = 3;

export const GUIDE_STEPS = [
  // 1 - Welcome (no icon badge by request)
  {
    id: "welcome",
    Icon: null,
    accent: colors.primary.dark,
    title: {
      he: "ברוכים הבאים ל-LeadifyAI",
      en: "Welcome to LeadifyAI",
    },
    body: {
      he: "**סיור קצר של דקה שיכיר לכם את הכלים החשובים במערכת, כדי שתפיקו ערך כבר מהיום הראשון.**\n\nאפשר לדלג בכל רגע, ולפתוח את הסיור שוב מהתפריט מתי שתרצו.",
      en: "**A one-minute tour of the tools that matter most, so you get value from day one.**\n\nYou can skip anytime, and reopen the tour whenever you like from the menu.",
    },
    media: null,
    cta: null,
    requires: [],
  },

  // 2 - DELETED (PR E, 2026-09-19): the "set a profession" step.
  //
  // It was the third surface asking the same question and the only one that
  // could not take the answer - deliberately CTA-less, because a link this
  // early let a user leave the tour after two steps and never see it again. The
  // welcome dialog (login 1) and the dashboard's inline `no-profession` empty
  // state now both ASK and SUBMIT, so the step had nothing left to add and the
  // tour no longer opens on login 1 at all.
  //
  // `CURRENT_GUIDE_VERSION` deliberately STAYS at 3: removing a step is not a
  // reason to re-show the whole tour to every existing user. Its capability key
  // `no_profession` was removed from CAPABILITY_PREDICATES with it.

  // 3 - Make the trial count (trial-gated via requires: ["trial"]).
  // Placed here on purpose: after the profession requirement and before the
  // feature tour, so a trialing user gets the roadmap for their 14 days before
  // the individual features are explained one by one.
  //
  // Every promise in this body is one the product keeps. "מחדד את ההתאמות שלכם"
  // is TRUE because responses are reviewed by hand to improve matching - it is
  // NOT a claim that the system learns automatically, and must not be reworded
  // into one. Marking a location irrelevant really does stop future
  // opportunities from that region; a public group really does add coverage.
  //
  // Deliberately NO cta, for the same reason as step 2: FeatureGuideDialog's
  // handleNavigate closes the guide and closeGuide stamps CURRENT_GUIDE_VERSION,
  // so a link on an early step would let the user leave the tour and never see
  // the rest of it. The dashboard's TrialBoostPanel carries the actual routes.
  {
    id: "trial-boost",
    Icon: Rocket,
    accent: "#7c3aed",
    iconInline: true,
    iconGradient: ["#a78bfa", "#7c3aed"],
    title: {
      he: "להוציא את המקסימום מתקופת הניסיון",
      en: "Get the most out of your trial",
    },
    body: {
      he: "14 ימים מספיקים כדי לראות בדיוק מה המערכת מביאה לכם - אם תעשו שלושה דברים: תיאור מקצוע מדויק, הוספת קבוצות פייסבוק ציבוריות, וסימון ההזדמנויות שאתם מקבלים. שלושתם ביחד משפרים ישירות את מה שיגיע אליכם בהמשך.",
      en: "14 days is enough to see exactly what the system brings you - if you do three things: write a precise profession description, add public Facebook groups, and mark the opportunities you receive. Together, all three directly improve what reaches you next.",
    },
    media: null,
    cta: null,
    requires: ["trial"],
  },

  // 4 - How leads are rated (heat rating).
  // iconInline: render the icon small + faded-gray at the start of the title,
  // instead of the badge above it.
  {
    id: "lead-rating",
    Icon: Flame,
    accent: "#ef4444",
    iconInline: true,
    iconGradient: ["#f97316", "#ef4444"],
    title: {
      he: "איך ההזדמנויות מדורגות?",
      en: "How are opportunities rated?",
    },
    body: {
      he: "כל הזדמנות מדורגת בסקאלה של 1-5.\nדירוג 5/5 מציין הזדמנות חזקה ביותר, כאשר כותב הפוסט ביקש שירות מפורש והשאיר פרטי התקשרות.\nדירוג 1/5 מציין בדרך כלל שיחה או שאלה כללית בתחום - הזדמנות מצוינת להגיב, להפגין מומחיות ולבנות מוניטין.",
      en: "Every opportunity is rated on a 1-5 scale.\nA 5/5 marks the strongest kind of opportunity - the poster explicitly asked for a service and left contact details.\nA 1/5 usually marks a general conversation or question in the field - a great opportunity to respond, show expertise, and build reputation.",
    },
    media: null,
    cta: null,
    requires: [],
  },

  // 5 - Push notifications (visible to everyone: requires []).
  // Sits right after the rating step because it answers the question that one
  // raises - "so how do I hear about an opportunity?" - before the dashboard
  // tour begins. Teal accent because the feature's own screen is the
  // notifications tab of Settings.
  //
  // Deliberately NO cta, for the reason spelled out on steps 2, 3 and 10:
  // FeatureGuideDialog's handleNavigate calls onClose, and closeGuide stamps
  // CURRENT_GUIDE_VERSION - so a link on step 5 of ~15 would let the user leave
  // the tour a third of the way in and never see it, or the rest of it, again.
  // (The step shape's cta is a PATH only; the button label is the guide-wide
  // "מעבר לעמוד" / "Go there", so a per-step label is not expressible here.)
  // The what's-new dialog - components/dashboard/PushNotificationsAnnounce.js -
  // is the surface that carries the route to /settings?tab=notifications.
  {
    id: "push-notifications",
    Icon: BellRing,
    accent: "#0d9488",
    iconInline: true,
    iconGradient: ["#2dd4bf", "#0d9488"],
    title: {
      he: "התראות Push - ישר לנייד",
      en: "Push notifications - straight to your phone",
    },
    body: {
      he: "כל הזדמנות חדשה מגיעה אליכם כהתראה בנייד, בזמן אמת וללא הגבלה יומית.\nמומלץ להתקין את האפליקציה ולאשר התראות - כך לא תפספסו הזדמנות, גם כשאתם לא מול המסך.\nהתראות המייל ממשיכות לעבוד כרגיל, כגיבוי.",
      en: "Every new opportunity reaches you as a notification on your phone, in real time and with no daily limit.\nWe recommend installing the app and allowing notifications - so you never miss an opportunity, even when you are away from the screen.\nEmail alerts keep working as usual, as a backup.",
    },
    media: null,
    cta: null,
    requires: [],
  },

  // 6 - The main dashboard (the KPI cards are the view switcher since the
  // LeadTabs pill row was folded into them, 2026-08-20; each card on its own
  // line). The step id stays "dashboard-tabs" - it may key seen-state, and
  // renaming would not change what the user sees (this dialog never anchors
  // to DOM elements).
  {
    id: "dashboard-tabs",
    Icon: LayoutGrid,
    accent: colors.primary.dark,
    iconInline: true,
    iconGradient: [colors.primary.main, colors.primary.dark],
    title: {
      he: "הדשבורד הראשי",
      en: "Your main dashboard",
    },
    body: {
      he: "לחיצה על כרטיסי המדדים שבראש הדשבורד מחליפה בין התצוגות:\n\n**הכל** - מציג את כל ההזדמנויות, כולל אלה שהשארת עליהם משוב.\n**הזדמנויות חמות** - הזדמנויות שמעידות על צורך אמיתי בשירות בתחום שלך (דירוג 3-5).\n**בניית מוניטין** - פוסטים שכדאי להגיב עליהם כדי לחזק את הנוכחות הדיגיטלית שלך ולצמוח באופן אורגני לאורך זמן (דירוג 1-2).\n**נוצר קשר** - הזדמנויות שסימנת שיצרת בהן קשר.",
      en: "Clicking the KPI cards at the top of the dashboard switches between the views:\n\n**All** - shows every opportunity, including ones you've left feedback on.\n**Hot Opportunities** - opportunities that signal a real need for a service in your field (rated 3-5).\n**Build Reputation** - posts worth replying to, to strengthen your digital presence and grow organically over time (rated 1-2).\n**Contacted** - opportunities you marked as contacted.",
    },
    media: null,
    cta: null,
    requires: [],
  },

  // 6b - Lead labels ("תוויות"), right after the dashboard step: labels are
  // the second way to slice the same feed. Owner-approved copy, verbatim.
  // Device-neutral on purpose (no anchor): the chip row exists only at sm+ and
  // only once the user has a label, the "תוויות" pill only at xs.
  //
  // Deliberately NO cta (a cta mid-tour closes the guide and stamps the
  // version) and NO CURRENT_GUIDE_VERSION bump: this step reaches NEW users;
  // existing users hear about labels from the one-shot LabelsAnnounce dialog.
  // `requires: ["labels"]` is the dashboard's own labels gate, so the tour never
  // describes a feature a lapsed account cannot see.
  {
    id: "lead-labels",
    Icon: Tags,
    // The `indigo` label colour (theme/labelPalette.js): dot / hi / deep.
    accent: "#4F46E5",
    iconInline: true,
    iconGradient: ["#7F86EE", "#3730A3"],
    title: {
      he: "תוויות - איפה כל עסקה עומדת?",
      en: "Labels - where does each deal stand?",
    },
    body: {
      he: "סמנו כל הזדמנות בתווית לפי שלב הטיפול - “ממתין לתשובה”, “הצעת מחיר נשלחה”, “נסגרה עסקה” ועוד.\nלחיצה על תווית מציגה רק את ההזדמנויות שבה.",
      en: "Mark each opportunity with a label for its stage - “Waiting for a reply”, “Quote sent”, “Deal closed” and more.\nClicking a label shows only the opportunities in it.",
    },
    note: {
      he: "התוויות גלויות רק לכם. אפשר לשנות שם, צבע וסדר, ולהוסיף עד 10.",
      en: "Your labels are visible only to you. You can rename, recolour and reorder them, and add up to 10.",
    },
    media: null,
    cta: null,
    requires: ["labels"],
  },

  // 7 - Post analysis via the brain button on the LeadCard (and inside lead details)
  {
    id: "post-analysis",
    Icon: Brain,
    accent: colors.analysis.icon,
    iconInline: true,
    iconGradient: [colors.analysis.iconBright, colors.analysis.icon],
    title: {
      he: "למה הפוסט זוהה כהזדמנות?",
      en: "Why this was identified as an opportunity?",
    },
    body: {
      he: "לחיצה על כפתור הניתוח (סמל המוח) שעל כרטיס ההזדמנות הופכת את חלון הפוסט ומציגה את ניתוח ה-AI וההמלצות - שם מוסבר בדיוק למה הפוסט זוהה כהזדמנות ואיך הכי כדאי לגשת אליו.\nאותו ניתוח זמין גם בחלון “צפה בפרטים”, בלחיצה על חלון הפוסט.",
      en: "Clicking the analysis button (the brain icon) on the opportunity card flips the post window to reveal the AI's analysis and recommendations - explaining exactly why the post was identified as an opportunity and how best to approach it.\nThe same analysis is also available inside “View details”, by clicking the post window.",
    },
    media: null,
    cta: null,
    requires: [],
  },

  // 8 - Compose a personalized message on demand (orange, matching the
  // "נסח הודעה" button in the opportunity window)
  {
    id: "personalized-message",
    Icon: MessageSquare,
    accent: "#ea580c",
    iconInline: true,
    iconGradient: ["#fb923c", "#ea580c"],
    title: {
      he: "הודעה אישית בלחיצת כפתור",
      en: "A personal message in one click",
    },
    body: {
      he: "בתוך חלון ההזדמנות, לחיצה על “נסח הודעה” תיצור עבורכם הודעה אישית מוכנה לשליחה שמנוסחת ע\"י ה-AI שלנו על סמך הפוסט והתיאור המקצועי שלכם, כדי שתוכלו לפנות במהירות ובאופן שמתאים בדיוק להזדמנות.",
      en: "Inside the opportunity window, clicking “Compose Message” drafts a ready-to-send personal message for you, phrased by our AI from the post and your professional description, so you can reach out fast and in a way that fits the opportunity perfectly.",
    },
    note: {
      he: "מספר ההודעות שניתן לנסח בחודש משתנה בהתאם לסוג המנוי שלכם.",
      en: "The number of messages you can compose each month depends on your plan.",
    },
    media: null,
    cta: null,
    requires: [],
  },

  // 9 - "Contacted" → follow-up reminder
  {
    id: "action-contacted",
    Icon: Phone,
    accent: "#22c55e",
    iconInline: true,
    iconGradient: ["#4ade80", "#16a34a"],
    title: {
      he: "“נוצר קשר” + תזכורת follow-up",
      en: "“Contacted” + a follow-up reminder",
    },
    body: {
      he: "מתחת לפוסט, בכרטיס ההזדמנות, מופיעה שורת כפתורים. לחיצה על “נוצר קשר” פותחת חלון שבו אפשר לקבוע תזכורת מעקב להזדמנות הזו, כדי שאף הזדמנות לא תיפול בין הכיסאות.",
      en: "Under the post on each opportunity card there's a row of buttons. Clicking “Contacted” opens a window where you can set a follow-up reminder for that opportunity, so none of them slips through the cracks.",
    },
    media: null,
    cta: null,
    // Follow-up reminders are a Professional/Enterprise feature (trialing users
    // keep them); hide this step from paid Solo users.
    requires: ["followup"],
  },

  // 10 - "Location not relevant" ("מיקום לא רלוונטי") → excluded regions
  {
    id: "action-exclude-location",
    Icon: MapPinOff,
    accent: "#f97316",
    iconInline: true,
    iconGradient: ["#fb923c", "#ea580c"],
    title: {
      he: "“מיקום לא רלוונטי”",
      en: "“Location not relevant”",
    },
    body: {
      he: "בשורת הכפתורים שמתחת לפוסט, לחיצה על “מיקום לא רלוונטי” תוסיף את האזור שממנו הגיעה ההזדמנות לרשימת האזורים המוחרגים שלכם - כך לא יתקבלו ממנו יותר הזדמנויות בעתיד. הכפתור “לא רלוונטי” שלצידו מסיר הזדמנות בודדת מהרשימה שלכם. תמיד אפשר לשנות זאת באזור האישי.",
      en: "In the row of buttons under the post, clicking “Location not relevant” adds the opportunity's region to your excluded areas - so no more opportunities will come from there. The “Not relevant” button beside it removes a single opportunity from your list. You can always change this in your Personal Area.",
    },
    media: null,
    cta: null,
    requires: [],
  },

  // 11 - Supporter network (supporter_network_eligible).
  // Sits at the END of the action block, right after the LeadCard button-row
  // steps ("נוצר קשר" / "מיקום לא רלוונטי") and before the business-website
  // step, because it IS one more thing that button row can do - the request is
  // sent from the opportunity card itself. Teal accent matched to the feature's
  // own screens (SupportersSettings / SupporterRequestDialog use #0d9488 ->
  // #14b8a6), so the card and the screens it describes read as one thread.
  //
  // Deliberately NO cta (owner decision): FeatureGuideDialog's handleNavigate
  // closes the guide and stamps CURRENT_GUIDE_VERSION, so a link here would let
  // a user leave the tour mid-way and never see the remaining steps. The
  // dashboard what's-new dialog that used to carry the route was removed; the
  // Settings "supporters" tab is reached from Settings itself and from the
  // supporter request dialog on an opportunity card.
  //
  // Body wording follows the guide's convention of softening caps to
  // non-numeric ("מכרים ולקוחות מרוצים", not "עד X") - the exact per-tier
  // numbers live on the pricing card and the FAQ.
  {
    id: "supporter-network",
    Icon: HeartHandshake,
    accent: "#0d9488",
    iconInline: true,
    iconGradient: ["#14b8a6", "#0d9488"],
    title: {
      he: "רשת התומכים שלי",
      en: "My supporter network",
    },
    body: {
      he: "צרפו מכרים ולקוחות מרוצים שיסכימו להמליץ עליכם - ובכל הזדמנות רלוונטית, בלחיצת כפתור אחת מכרטיס ההזדמנות, הם יקבלו מייל קצר עם קישור לפוסט ויוכלו להמליץ עליכם בתגובות. המלצה אישית בתגובות מכפילה את הסיכוי לסגור עסקה.",
      en: "Add contacts and happy customers who agree to vouch for you - and on any relevant opportunity, with a single click from the opportunity card, they'll get a short email with a link to the post and can recommend you in the comments. A personal recommendation in the comments doubles your chances of closing the deal.",
    },
    note: {
      he: "חובה לוודא עם התומך קודם הוספת המייל שלו. כל תומך יצטרך לאשר את הצטרפותו לרשת התומכים שלכם במייל לפני שנשלחת אליו בקשה ראשונה.",
      en: "You must check with your supporter before adding their email address. Every supporter has to confirm joining your supporter network by email before a first request is ever sent to them.",
    },
    media: null,
    cta: null,
    requires: ["supporters"],
  },

  // 12 - Business website builder (website_eligible: Professional/Enterprise +
  // trialing users). Neutral wording so it reads right for both — the note
  // carries the plan/trial nuance. Icon matches the navbar entry (PanelsTopLeft).
  {
    id: "business-website",
    Icon: PanelsTopLeft,
    accent: colors.primary.dark,
    iconInline: true,
    iconGradient: [colors.primary.main, colors.primary.dark],
    title: {
      he: "אתר עסקי מקצועי",
      en: "A professional business site",
    },
    body: {
      he: "בנו אתר עסקי מקצועי עם קישור אישי משלכם, שמופיע גם בגוגל - עוד דרך להשיג הזדמנויות. בוחרים תבנית, מתאימים צבעים וגופנים, ומפרסמים בכמה דקות.",
      en: "Build a professional business site with your own personal link that also shows up on Google - another way to win opportunities. Pick a template, tune the colors and fonts, and publish in minutes.",
    },
    note: {
      he: "כלול במסלולי Professional ו-Enterprise. בתקופת הניסיון אפשר לבנות, לפרסם ולהתנסות בחינם.",
      en: "Included in the Professional and Enterprise plans. During your trial you can build, publish and try it for free.",
    },
    media: null,
    cta: { path: "/website" },
    requires: ["website"],
  },

  // 13 - Additional notification recipients (Professional/Enterprise only).
  // Hidden from Solo/Trial (they get 0 extra recipients) via requires.
  {
    id: "additional-recipients",
    Icon: MailPlus,
    accent: "#2563eb",
    iconInline: true,
    iconGradient: ["#60a5fa", "#2563eb"],
    title: {
      he: "נמענים נוספים להתראות",
      en: "Extra notification recipients",
    },
    body: {
      he: "אפשר להוסיף כתובות אימייל נוספות שיקבלו התראה על כל הזדמנות חדשה, במקביל לכתובת הראשית שלכם - מושלם לשותפים, לצוות המשרד או לאיש קשר נוסף שתרצו לשמור מעודכן.\nההגדרה נמצאת תחת הגדרות > מתקדם, באזור “כתובות אימייל נוספות”.",
      en: "You can add more email addresses that get notified about every new opportunity, alongside your main address - perfect for partners, your office team, or another contact you want to keep in the loop.\nFind it under Settings > Advanced, in the “Additional Email Addresses” section.",
    },
    note: {
      he: "מספר הנמענים הנוספים משתנה בהתאם לסוג המנוי שלכם.",
      en: "The number of additional recipients depends on your plan.",
    },
    media: null,
    cta: { path: "/settings?tab=advanced" },
    requires: ["additional_emails"],
  },

  // 14 - Business Intelligence (only if the user has it enabled)
  {
    id: "business-intelligence",
    Icon: Aperture,
    accent: colors.businessIntelligence.accent,
    iconInline: true,
    iconGradient: ["#F9A8D4", "#EC4899"],
    title: {
      he: "מודיעין עסקי",
      en: "Business Intelligence",
    },
    body: {
      he: "עקבו אחר אזכורים של המותג שלכם ושל המתחרים, ואחר אירועים חריגים בתעשייה - יתרון תחרותי שמאפשר לכם לזהות מגמות, להגיב בזמן אמת ולהוביל את השוק.",
      en: "Track mentions of your brand and competitors, and unusual events in your industry - a competitive edge that lets you spot trends, respond in real time and lead your market.",
    },
    media: null,
    cta: null,
    requires: ["bi"],
  },

  // 15 - Online Interventions (only if the user has it enabled)
  {
    id: "interventions",
    Icon: Fingerprint,
    accent: colors.intervention.accent,
    iconInline: true,
    iconGradient: ["#5EEAD4", "#00A896"],
    title: {
      he: "התערבות בשיח ברשת",
      en: "Online Interventions",
    },
    body: {
      he: "הפעלת התערבות אותנטית בשיח רלוונטי במטרה לחזק את נוכחות החברה בשיח, לשמור על מוניטין חיובי, להשפיע על החלטות רכישה ולהגדיל הזדמנויות המרה.",
      en: "Launch authentic interventions in relevant conversations - to strengthen your company's presence, maintain a positive reputation, influence purchase decisions and increase conversion opportunities.",
    },
    media: null,
    cta: null,
    requires: ["intervention"],
  },
];
